import axios from 'axios';
import { useContext, useState, useEffect } from 'react';

import { ModalContext } from 'app/app';
import { FieldInput } from 'components/fieldInput';
import { IconedButton } from 'components/IconedButton';
import {
  Animate,
  Grid,
  Search,
  Table,
  useNavigate,
  ViewContext,
} from 'components/lib';

import styles from './styles.module.scss';
import debounce from 'helpers/debounce';
import DateUtils from '../../helpers/dateUtils';
import InputUtils from '../../helpers/inputUtils';
import { ChevronLeft, ChevronRight } from 'feather-icons-react/build/IconComponents';
import { useSearchParams } from "react-router-dom";

const dateUtils = new DateUtils();
const inputUtils = new InputUtils();

export function ServiceCenter() {
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);
  const [payload, setPayload] = useState();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQueryString, setSearchQueryString] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [ticketStatusFilter, setTicketStatusFilter] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 1;
  const [page, setPage] = useState(initialPage);

  async function fetchTickets(page, search, status, ticketStatus, pageSize) {
    setLoading(true);
    setSearchParams({ page });
    await axios
      .get('/api/tickets/admin', {
        params: {
          page,
          search,
          status,
          ticketStatus: ticketStatus.length > 0 ? ticketStatus : undefined,
          pageSize,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      })
      .then((response) => {
        if (response.data && response.data.success) {
          setPayload(response.data);
          const formattedTickets = response.data.data.map(
            (ticket) => ({
              id: ticket.id,
              Number: ticket.ticketNumber,
              'Agency name': ticket.agencyName && ticket.agencyId ? (
                <a
                  href={
                    window.location.host === 'localhost:5002'
                      ? `http://localhost:5002/admin/agency/${ticket.agencyId}`
                      : `https://admin.coverbadger.com/admin/agency/${ticket.agencyId}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    color: 'blue',
                    textDecoration: 'underline',
                  }}
                >
                  {ticket.agencyName}
                </a>
              ) : (
                ticket.agencyName || 'N/A'
              ),
              'Company Name':
                ticket.companyName &&
                inputUtils
                  .capitalizeFirstLetter(ticket.companyName)
                  .replaceAll('Llc', 'LLC'),
              'Submission Number': ticket.submissionNumber ? (
                ticket.submissionNumber
              ) : (
                'N/A'
              ),
              'Created At':
                ticket.createdAt &&
                dateUtils.timestamp(ticket.createdAt),
              'Updated At':
                ticket.updatedAt &&
                dateUtils.timestamp(ticket.updatedAt),
              Status: ticket.ticketStatus && ticket.ticketStatus,
              ticketStatus: ticket.ticketStatus,
              status: ticket.status,
            })
          );
          setTickets(formattedTickets);
        } else {
          context.notification.show(
            'Unexpected API response structure',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      })
      .catch(() => {
        context.notification.show(
          'Failed to load tickets',
          'error',
          true,
          'toast',
          'x-circle'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    fetchTickets(
      page,
      searchQueryString,
      statusFilter,
      ticketStatusFilter,
      pageSize
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQueryString, statusFilter, ticketStatusFilter, pageSize]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const debouncedSetSearchQueryString = debounce((query) => {
    setSearchQueryString(query);
    setPage(1);
  }, 500);

  return (
    <Animate type="pop">
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex' }}>
          {['Active', 'Finished'].map((status) => (
            <div
              key={status}
              style={{
                marginLeft: '5px',
                width: '150px',
                backgroundColor:
                  statusFilter === status ? 'black' : '#EAEAEA',
                color:
                  statusFilter === status ? 'white' : '#090E3480',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 10,
                fontSize: 16,
                fontWeight: 600,
                borderTopLeftRadius: 10,
                borderTopRightRadius: 10,
                cursor: 'pointer',
              }}
              onClick={() => handleStatusFilter(status)}
            >
              {status === 'Finished' ? 'Completed' : status}
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <IconedButton
            size="150px"
            color="#d32425"
            textColor="white"
            icon="plus-square"
            text="Create Ticket"
            marginRight
            action={async () => {
              setModal({
                name: 'service_center_modal',
                action: () => {
                },
              });
            }}
          />
        </div>
      </div>
      <Grid cols="1">
        <div className={styles.wrapper} style={{ marginTop: 0 }}>
          <div
            style={{
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ width: '80%', marginTop: 30 }}>
              <Search
                callback={(query) => {
                  debouncedSetSearchQueryString(query);
                }}
              />
            </div>
            <div style={{ width: '20%', marginLeft: 20 }}>
              <FieldInput
                id="ticketStatus_filter"
                type="multi_select"
                label="Filters"
                defaultValue={[]}
                options={[
                  { value: 'Pending', label: 'Pending' },
                  { value: 'In Progress', label: 'In Progress' },
                ]}
                handleChange={(ticketStatus) => {
                  setTicketStatusFilter(
                    ticketStatus.map((status) => {
                      return status.value;
                    })
                  );
                  setPage(1);
                }}
              />
            </div>
          </div>

          <Table
            data={tickets}
            loading={loading}
            badge={{
              col: 'Status',
              color: 'gray',
              condition: [
                { value: 'Pending', color: 'gray' },
                { value: 'In Progress', color: 'blue' },
                { value: 'Completed', color: 'green' },
              ],
            }}
            hide={[
              'id',
              'assignedUserEmail',
              'userId',
              'ticketStatus',
              'status',
            ]}
            actions={{
              custom: [
                {
                  icon: 'search',
                  color: '#637381',
                  action: (row) => {
                    navigate(
                      `/admin/service_center/ticket/${row.id}?page=${page}`
                    );
                  },
                },
                {
                  icon: 'check-square',
                  color: '#637381',
                  action: (row) => {
                    setModal({
                      name:
                        row.status === 'Active'
                          ? 'complete_ticket_modal'
                          : 'reopen_ticket_modal',
                      data: {
                        id: row.id,
                        status: row.status,
                        ticketStatus: row.ticketStatus,
                      },
                    });
                  },
                },
              ],
            }}
          />
          {/* Pagination */}
          {payload && !loading && (
            <div className="flex items-center justify-between mb-2 mt-6 sm:mb-0">
              <div className="flex items-center">
                <span className="mr-2">Items per page</span>
                <select
                  className="
                  border bg-white border-gray-300
                  rounded-md shadow-sm p-2"
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(Number(e.target.value));
                  }}
                >
                  {[10, 20, 30, 50, 100].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  className="
                    p-2 rounded-xl bg-gray-100
                    text-gray-500 hover:bg-gray-200
                    disabled:opacity-50"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft size={20} />
                </button>

                <span className="flex items-center">
                  <span className="mr-1">page</span>
                  <input
                    type="number"
                    min={1}
                    max={payload.pages}
                    defaultValue={page}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const newPage = e.target.value ? Number(e.target.value) : 1;
                        setPage(newPage);
                      }
                    }}
                    className="
                      w-16 p-2 rounded-xl border
                      border-gray-100 text-center"
                  />
                  <span className="ml-1">of {payload.pages}</span>
                </span>

                <button
                  className="
                    p-2 rounded-xl bg-gray-100
                    text-gray-500 hover:bg-gray-200
                    disabled:opacity-50"
                  onClick={() => setPage((prev) => Math.min(prev + 1, payload.pages))}
                  disabled={page === payload.pages}
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              <div className="flex items-center">
                <span>Total items: {payload.total}</span>
              </div>
            </div>
          )}
        </div>
      </Grid>
    </Animate>
  );
}
